import React, { useState, useRef } from 'react'
import { Send, Trash2, Play, Pause, RotateCcw, Loader2, Sparkles, Wand2, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import GeoGebraSimulation from '../../components/interactive/shared/simulations/GeoGebraSimulation'
import { API_CONFIG } from '../../config/api.config'

const AILab: React.FC = () => {
    const navigate = useNavigate()
    const [prompt, setPrompt] = useState('')
    const [loading, setLoading] = useState(false)
    const [script, setScript] = useState('')
    const [history, setHistory] = useState('') // Store previous commands
    const [error, setError] = useState<string | null>(null)
    const ggbApiRef = useRef<any>(null)

    const handleGenerate = async () => {
        if (!prompt.trim()) return

        setLoading(true)
        setError(null)
        try {
            const response = await fetch(`${API_CONFIG.python.apiUrl}/ai/geogebra-gen`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    prompt,
                    history: history // Send history for incremental updates
                })
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.detail || 'Failed to generate script')
            }

            const data = await response.json()
            const newScript = data.script
            setScript(newScript)
            
            // Append to history
            setHistory(prev => prev + '\n' + newScript)
            
            if (ggbApiRef.current && newScript) {
                // Execute commands line by line
                const commands = newScript.split('\n')
                commands.forEach((cmd: string) => {
                    if (cmd.trim() && !cmd.trim().startsWith('//')) {
                        console.log("Executing GGB Command:", cmd.trim())
                        ggbApiRef.current.evalCommand(cmd.trim())
                    }
                })
            }
            setPrompt('') // Clear prompt after success
        } catch (err: any) {
            console.error(err)
            setError(err.message || 'Có lỗi xảy ra khi tạo mô phỏng')
        } finally {
            setLoading(false)
        }
    }

    const handleReset = () => {
        if (ggbApiRef.current) {
            ggbApiRef.current.reset()
            setScript('')
            setHistory('')
        }
    }

    const handleClear = () => {
        if (ggbApiRef.current) {
            const allObjects = ggbApiRef.current.getAllObjectNames()
            allObjects.forEach((obj: string) => ggbApiRef.current.deleteObject(obj))
            setScript('')
            setHistory('')
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => navigate('/')}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg text-white">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-800 leading-none">AI Physics Lab</h1>
                            <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider font-semibold">Virtual Simulation Assistant</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <button 
                        onClick={handleReset}
                        className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                        title="Đặt lại bản vẽ"
                    >
                        <RotateCcw className="w-4 h-4" />
                        <span className="hidden sm:inline">Đặt lại</span>
                    </button>
                    <button 
                        onClick={handleClear}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                        title="Xóa tất cả"
                    >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Xóa hết</span>
                    </button>
                </div>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Left Panel: Chat & Controls */}
                <div className="w-full lg:w-[380px] bg-white border-r border-slate-200 flex flex-col shadow-inner">
                    <div className="p-5 flex-1 overflow-y-auto space-y-6">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 shadow-sm">
                            <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                                <Wand2 className="w-4 h-4 text-blue-600" /> Gợi ý yêu cầu
                            </h3>
                            <div className="space-y-2">
                                {[
                                    'Vẽ một con lắc đơn dao động với chiều dài 3m',
                                    'Mô phỏng vật ném ngang từ độ cao 10m với vận tốc 5m/s',
                                    'Vẽ đồ thị hàm số y = 2sin(x) + 3',
                                    'Vẽ một tam giác ABC có A(0,0), B(3,0), C(0,4)'
                                ].map((suggestion, idx) => (
                                    <button 
                                        key={idx}
                                        onClick={() => setPrompt(suggestion)}
                                        className="w-full text-left text-xs text-blue-700 hover:bg-blue-100/50 p-2 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                                    >
                                        • "{suggestion}"
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 px-1">Yêu cầu của bạn</label>
                            <textarea 
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Nhập yêu cầu mô phỏng vật lý tại đây..."
                                className="w-full h-40 p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none text-sm shadow-sm bg-slate-50/50"
                            />
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 animate-pulse">
                                ⚠️ {error}
                            </div>
                        )}

                        {script && (
                            <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest px-1">GGBScript được tạo</label>
                                <div className="relative">
                                    <pre className="p-4 bg-slate-900 text-green-400 rounded-xl text-[11px] overflow-x-auto max-h-60 font-mono border border-slate-800 shadow-lg">
                                        {script}
                                    </pre>
                                    <div className="absolute top-2 right-2 flex gap-1">
                                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-5 border-t border-slate-100 bg-white">
                        <button 
                            onClick={handleGenerate}
                            disabled={loading || !prompt.trim()}
                            className={`w-full py-4 px-6 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all transform active:scale-95 ${
                                loading || !prompt.trim() 
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' 
                                : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800 shadow-xl shadow-blue-200'
                            }`}
                        >
                            {loading ? (
                                <><Loader2 className="w-5 h-5 animate-spin" /> Đang xử lý AI...</>
                            ) : (
                                <><Sparkles className="w-5 h-5" /> Tạo mô phỏng</>
                            )}
                        </button>
                    </div>
                </div>

                {/* Right Panel: GeoGebra View */}
                <div className="flex-1 bg-slate-100 p-4 lg:p-6 flex items-center justify-center">
                    <div className="w-full h-full max-h-[85vh] shadow-2xl rounded-3xl overflow-hidden bg-white border border-white relative">
                        <div className="absolute top-4 right-4 z-10 flex gap-2">
                             <div className="px-3 py-1 bg-white/80 backdrop-blur shadow-sm rounded-full text-[10px] font-bold text-slate-600 border border-slate-200">
                                GeoGebra Engine v5.0
                             </div>
                        </div>
                        <GeoGebraSimulation 
                            filename="" // Empty to start blank
                            title=""
                            showCustomControls={true}
                            height="100%"
                            onReady={(api) => {
                                ggbApiRef.current = api
                                console.log("AI Lab: GeoGebra API Instance Ready")
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AILab
